import React, {ChangeEvent, useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button, Pagination, Popover, Table as AntTable, Tag, Tooltip} from 'antd';
import {FunnelSimpleIcon} from '@phosphor-icons/react';
import {SitesHeader, SitesTableSearchBar} from '../../../index';
import siteService from '../../../../services/siteService';
import {useViewerStore} from '../../../../store/store';
import Filter from '../Filter/Filter';
import {getDateinMM_DD_YYYY} from '../../../../helpers/dateHelper';
import {ThemeColorContext} from '../../../../contexts/ThemeColorContext';
import { logger } from '../../../../services/loggingService';
import { DEFAULT_DATE_FROM } from "../../../../Constants";
import type { ColumnType } from "antd/es/table";
import { SiteRow } from '../../../../@types/types';
import { AlertType } from '../../../../@types/enums';
import { ViewerState } from '../../../../@types/store';

const SitesTable: React.FC = () => {
    const urlParams = new URLSearchParams(window.location.search);

    const {primary} = useContext(ThemeColorContext);
    const navigate = useNavigate();

    //Store
    const stageSelectedFilter = useViewerStore(state => state.stageSelectedFilter);
    const dateSelectedFilter = useViewerStore(state => state.dateSelectedFilter);
    const isDateFilterApplied = useViewerStore(state => state.isDateFilterApplied);
    const isStageFilterApplied = useViewerStore(state => state.isStageFilterApplied);
    const isAppliedFilter = useViewerStore(state => state.isApplyFilter);

    //Local States
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(parseInt(urlParams.get('page') ?? '1', 10) ?? 1);
    const [tableLoading, setTableLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [siteData, setSiteData] = useState([]);

    const [tableParams, setTableParams] = useState({
        pagination: {
            current: page,
            pageSize: 0,
            total: 0,
        },
    });

    //Columns for the table
    const columns: ColumnType<SiteRow>[] = [
        {
            title: "Address",
            dataIndex: "address",
            key: "address",
            render: (_text, record) => (
                <a
                    onClick={() => handleEditClick(record)}
                    style={{ padding: 0, cursor: "pointer" }}
                >
                    {record.address}
                </a>
            ),
        },
        {
            title: 'Multiplier',
            dataIndex: 'multiplier',
            key: 'multiplier',
            width: '8%'

        },
        {
            title: 'Opportunity Name',
            dataIndex: 'opportunityName',
            key: 'opportunityName',

        },
        {
            title: 'Stage',
            dataIndex: 'stage',
            key: 'stage',
        },
        {
            title: 'Created Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            render: (text) => <span>{getDateinMM_DD_YYYY(text).date}</span>,
        },
    ];

    //functionality for storing the searched data
    const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);

        if (!isDateFilterApplied) {
            if (value) {
                useViewerStore.setState({
                    dateSelectedFilter: {
                        dateFrom: DEFAULT_DATE_FROM,
                        dateTo: null
                    }
                });
            } else {
                useViewerStore.setState({
                    dateSelectedFilter: {
                        dateFrom: DEFAULT_DATE_FROM,
                        dateTo: null
                    }
                });
            }
        }
    }

    const getForamattedData = (data: any) => {
       return data.Data.map((item: any, index: number) => {
            const [state, zipCode] = item.State?.split('-') ?? [];

            return {
                key: index,
                address: item.Address,
                opportunityName: item.Opportunity_Name,
                salesPerson: item.SalesPerson,
                stage: item.Stage,
                createdDate: item.CreatedDate,
                state: state || '',
                zipCode: zipCode || '',
                directLead: item.DirectLead,
                account_Id: item.Account_Id,
                salesForceId: item.SalesForceId,
                multiplier: item.Multiplier,
                data_Source: item.Data_Source
            }
        });
    }

    const search = async (query: string, page: number) => {
        setTableLoading(true);

        try {
            const data = await siteService.postSitesSearch(searchValue, page, stageSelectedFilter, dateSelectedFilter, isDateFilterApplied, isStageFilterApplied);
            if (data.Message === 'No records found') {
                setSiteData([]);
                return;
            }

            setSiteData(getForamattedData(data));

            setTableParams({
                pagination: {
                    current: data.PageNumber,
                    pageSize: data.PageSize,
                    total: data.TotalRecords,
                }
            });
        } catch (error: any) {
            logger.error(error);
            if (error.response && (error.response.status >= 400 && error.response.status < 600)) {
                setSiteData([]);
            }
        } finally {
            setTableLoading(false);
        }
    }

    //functionality for handling the search bar
    const handleSearch = () => {
        search(searchValue.trim(), 1);
    };

    const handlePageChange = (page: number) => {
        const url = new URL(window.location.href);
        url.searchParams.set('page', page.toString());
        window.history.pushState({}, '', url);
        setPage(page);
    }

    //functionality that triggers the api call at the beginning
    useEffect(() => {
        search(searchValue.trim(), page);
    }, [page, isAppliedFilter]);

    const handleEditClick = (data: SiteRow) => {
        sessionStorage.setItem('SalesForceId', data.salesForceId);
        sessionStorage.setItem('currentStage', String(parseInt(data.stage.split(':')[0])));
        useViewerStore.setState({
            treeCartItems : [], 
            pathname : '/configure/roof', 
            viewerAlert: {
                show: false,
                title: "",
                message: "",
                messageType: AlertType.info,
                isModal: false
            },
            isStreetViewVisible:false,
            loadingCompleted: false
        });
        //Navigating to the editor page
        navigate(`/configure?sfid=${data.salesForceId}`);
    }


    const handleOpenChange = (newOpen: boolean) => {setOpen(newOpen);};

    const handleResetSearch = () => {
        setSearchValue('');
        useViewerStore.setState({
            stageSelectedFilter: [],
            dateSelectedFilter: {
                dateFrom: DEFAULT_DATE_FROM,
                dateTo: null
            },
            isStageFilterApplied: false,
            isDateFilterApplied: false
        })
        useViewerStore.setState((prev: ViewerState) => ({isApplyFilter: !prev.isApplyFilter}));
    }
    useEffect(()=>{
        document.title = `TS-OneDraw Sites Table `;
        useViewerStore.setState({isLeftSidebarOpen:false});
    },[])

    return (
        <>
            <SitesHeader>
                <SitesTableSearchBar
                    name='search'
                    placeholder='Search...'
                    value={searchValue}
                    onSearch={handleSearch}
                    onReset={handleResetSearch}
                    onChange={(e) => onSearchChange(e)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleSearch();
                        }
                    }}
                />

                <Tooltip
                    color={primary}
                    title='Click To Open Filter Options'
                    placement='right'
                >
                    <Popover
                        content={<Filter/>}
                        title='Filter Options'
                        trigger='click'
                        open={open}
                        onOpenChange={handleOpenChange}
                    >
                        <Button
                            type='primary'
                            size='large'
                            icon={<FunnelSimpleIcon size={24}/>}
                        >
                            Filter
                        </Button>
                    </Popover>
                </Tooltip>
            </SitesHeader>

            <AntTable
                rowKey={record => record.key}
                className={'sites-table'}
                loading={tableLoading}
                dataSource={siteData}
                scroll={
                    {
                        y: 600,
                        x: 'max-content'
                    }
                }
                bordered
                columns={columns}
                pagination={false}
                size='small'
                expandable={{
                    expandedRowRender: (record) =>
                        <ul className='sitesTableSubList'>
                            <li>
                                <span>Salesperson: {record.salesPerson}</span>
                            </li>
                            <li>
                                <span>Salesforce ID: {record.salesForceId}</span>
                            </li>
                            <li>
                                <span>Account ID: {record.account_Id}</span>
                            </li>
                            <li>
                                <span>Data Source: {record.data_Source}</span>
                            </li>
                        </ul>
                    ,
                    rowExpandable: (record) => record.name !== 'Not Expandable',
                }}
                locale={
                    {
                        emptyText: tableLoading ? 'Loading...' : 'No Records Found'
                    }
                }

            />
            <br/>
            <Pagination
                style={{textAlign: 'right'}}
                responsive
                onChange={handlePageChange}
                showTotal={total => `Current Page Records :
                ${tableParams.pagination.pageSize}
                & Total Records : ${total}`}
                showSizeChanger={false}
                defaultCurrent={page}
                pageSize={tableParams.pagination.pageSize}
                total={tableParams.pagination.total}/>
        </>
    );
}

export default SitesTable;
